import React, { useContext as useContextCore } from 'react'
import merge from 'lodash/merge'
import type { ChatMessage, ChatSender, File, User, UserProfile } from '@ztechapps/common.contract'
import type { ChatComponentProps, ChatComponents } from '../Chat/chatComponents'
import type { UseSendChatMessageResult } from '@ztechapps/chat.common'
import { useError, useUser } from '@ztechapps/common.hook'
import { generateId } from '@ztechapps/common.utils'
import { useChatMessage, useChatMessageSubscription } from '@ztechapps/chat.common'
import { chatComponents } from '../Chat/chatComponents'
import { ChannelSendButton } from './ChannelSendButton'
import { ComposerOtherButtons } from './ComposerOtherButtons'
import { appendMessageLine } from './appendMessageLine'
import { useFormalizeMessage } from './useFormalizeMessage'

export type ChannelTypesComponent = ChatComponents
export type ChannelMiddlewares = {
  afterSend?: ({ sendResult }: { sendResult: any }) => Promise<void>
}
export type ChannelProviderProps = {
  rootUrl?: string
  conversationId: string
  subject?: string
  recipient?: { email?: string; name?: string }
  components?: ChannelTypesComponent
  hooks?: ChannelMiddlewares
  members?: User[]
  // An external busy signal folded into `status.busy` alongside sending/formalizing — e.g. the
  // inbox's internal-note save, which has no ChannelProvider mutation of its own.
  disabled?: boolean
  // When set (storefront call sites), the AI "Formalize with AI" wand is suppressed.
  // Admin call sites omit it, so the wand stays.
  hideFormalizeButton?: boolean
  children: React.ReactNode
  sendChatMessageResult: UseSendChatMessageResult
}

export type ChannelContext = {
  message: string
  setMessage: React.Dispatch<React.SetStateAction<string>>
  setPendingImageUploads: React.Dispatch<React.SetStateAction<number>>
  status: MessageStatus
  messages: ChatMessage[]
  /** The thread's conversation code — names the detail jump without its own lookup. */
  conversationCode?: string
  /** The loaded chat's `_id` — the id an internal note is saved against (`conversationId` is its `externalId`). */
  chatId?: string
  chatInputRef: React.MutableRefObject<any>
  onRefresh: () => Promise<void>
  onSend: ({ isPublic, format }: { isPublic: boolean; format?: string }) => Promise<void>
  onFormalize: (content: string) => Promise<string>
  insertMessage: (newMessage: string) => void
  attachments: File[]
  addAttachments: (files: File[]) => void
  removeAttachment: (fileId: string) => void
} & Omit<ChannelProviderProps, 'children'>

export type MessageStatus = {
  errorSending?: Error
  sending: boolean
  loading: boolean
  errorLoading?: Error
  formalizing: boolean
  // Sending, formalizing and the provider's disabled flag lock the editor itself; an image
  // still uploading does not — typing continues while the upload runs.
  editorLocked: boolean
  // Sending and formalizing must disable the same controls — a send mid-formalize clears
  // the draft, then the formalized text resurrects it; an attach or template pick meanwhile gets
  // overwritten. An image still uploading joins the same gate — a send mid-upload would carry
  // a blob address instead of the stored one. One derived flag keeps every consumer's gate in sync.
  busy: boolean
}

const context = React.createContext<ChannelContext>({} as ChannelContext)
const CoreProvider = context.Provider

export const ChannelProvider = ({
  children,
  conversationId,
  subject,
  recipient,
  components,
  hooks,
  sendChatMessageResult,
  disabled,
  ...props
}: ChannelProviderProps) => {
  const chatInputRef = React.useRef(null)
  const [message, setMessage] = React.useState('')
  const [attachments, setAttachments] = React.useState<File[]>([])
  const [pendingImageUploads, setPendingImageUploads] = React.useState(0)
  const {
    messages,
    loading,
    error: errorLoading,
    onRefresh,
    setMessages,
    document,
  } = useChatMessage({
    conversationId,
  })

  useChatMessageSubscription({
    filter: {
      chat: {
        _id: conversationId,
      },
    },
    onData: () => onRefresh(),
  })

  const { sendChat, loading: sending, error: errorSending } = sendChatMessageResult
  const { user } = useUser()
  const { formalizing, onFormalize } = useFormalizeMessage()
  const editorLocked = sending || formalizing || Boolean(disabled)
  const status = {
    errorSending,
    sending,
    loading,
    errorLoading,
    formalizing,
    editorLocked,
    busy: editorLocked || pendingImageUploads > 0,
  }

  useError(errorSending)

  const channelDefaultComponents = {
    //input: RichTextChatInput,
    sendMessageButton: ChannelSendButton,
    chatActions: {
      otherButtons: (props: ChatComponentProps) => (
        <>
          <ComposerOtherButtons />
          {chatComponents?.chatActions?.otherButtons?.(props)}
        </>
      ),
    },
  }

  //always merge the components
  const newComponents: ChannelTypesComponent = merge({}, chatComponents, channelDefaultComponents, components)

  //set the stateful messages, with proper sorting

  const insertMessage = (newMessage: string) => {
    // Append through the functional update so a pick always lands below the latest staged
    // text even when this closure was created before the last keystroke re-rendered.
    setMessage((prev) => appendMessageLine(prev, newMessage))
  }

  //send the message
  const onSend = async ({ isPublic, format }) => {
    const sendResult = await sendChat({
      extenalId: conversationId,
      content: message,
      attachments,
      subject,
      isPublic,
      format,
      sender: {
        profile: {
          email: recipient?.email,
          name: recipient?.name,
        } as UserProfile,
      },
    })

    //do optimistic response
    const optimisticResponse = {
      attachments,
      content: message,
      sender: user as ChatSender,
      timeStamp: new Date(),
      _id: generateId(),
    } as ChatMessage

    setMessages([optimisticResponse, ...messages])

    //call the callback middlewares
    if (hooks?.afterSend) {
      await hooks.afterSend({ sendResult })
    }

    setMessage('')
    setAttachments([])
  }

  const addAttachments = (files: File[]) => setAttachments((prev) => [...prev, ...files])
  const removeAttachment = (fileId: string) => setAttachments((prev) => prev.filter((file) => file._id !== fileId))

  return (
    <CoreProvider
      value={{
        conversationId,
        message,
        setMessage,
        setPendingImageUploads,
        messages,
        conversationCode: document?.code,
        chatId: document?._id,
        status,
        onSend,
        onFormalize,
        components: newComponents,
        onRefresh,
        hooks,
        recipient,
        subject,
        chatInputRef,
        insertMessage,
        attachments,
        addAttachments,
        removeAttachment,
        sendChatMessageResult,
        disabled,
        ...props,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useChannelContext = () => useContextCore(context)
